import React, { useEffect, useState, useRef } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import api from "@/lib/api";
import { Search, HelpCircle, LogOut, ChevronDown, Users, GraduationCap, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { BrandMark } from "@/components/BrandMark.jsx";

const TOP_TABS = [
  { key: "hiring", label: "Hiring", icon: Briefcase, paths: ["/admin/forms", "/admin/candidates", "/admin/outreach"] },
  { key: "training", label: "Training", icon: GraduationCap, paths: ["/admin/training/modules", "/admin/training/tracks", "/admin/training/progress"] },
  { key: "people", label: "People", icon: Users, paths: ["/admin/team", "/admin/employees", "/admin/onboarding/new-joiners", "/admin/onboarding/checklists", "/admin/leaves", "/admin/tickets", "/admin/policies", "/admin/hr-email", "/admin/digest"] },
];

const SUB_TABS = {
  hiring: [
    { to: "/admin/forms", label: "Forms" },
    { to: "/admin/candidates", label: "Candidates" },
    { to: "/admin/outreach", label: "Outreach" },
  ],
  training: [
    { to: "/admin/training/modules", label: "Modules" },
    { to: "/admin/training/tracks", label: "Tracks" },
    { to: "/admin/training/progress", label: "Progress" },
  ],
  people: [
    { to: "/admin/team", label: "Team" },
    { to: "/admin/freelancers", label: "Freelancers" },
    { to: "/admin/onboarding/new-joiners", label: "Onboarding" },
    { to: "/admin/leaves", label: "Leaves" },
    { to: "/admin/tickets", label: "Tickets" },
    { to: "/admin/policies", label: "Policies" },
    { to: "/admin/hr-email", label: "Team inbox" },
    { to: "/admin/digest", label: "Digest" },
  ],
};
function todayLabel() {
  const d = new Date();
  const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  return `${String(d.getDate()).padStart(2,"0")} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export default function AdminShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [menu, setMenu] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const inputRef = useRef(null);

  const isIC = user?.role === "internal_committee";
  const activeTop = TOP_TABS.find((t) => t.paths.some((p) => loc.pathname.startsWith(p)));

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault(); setShowSearch(true); setTimeout(() => inputRef.current?.focus(), 0);
      }
      if (e.key === "Escape") setShowSearch(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  useEffect(() => {
    if (!q || q.length < 2) { setResults(null); return; }
    let active = true;
    const t = setTimeout(async () => {
      try { const r = await api.get("/search", { params: { q } }); if (active) setResults(r.data); } catch {}
    }, 180);
    return () => { active = false; clearTimeout(t); };
  }, [q]);

  const viewAs = async (role) => {
    try {
      await api.post("/auth/view-as", { role });
      toast.success(`Viewing as ${role}`); setMenu(false);
      window.location.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F4F4F5] flex flex-col">
      <header className="sticky top-0 z-30 bg-[#0A0A0A] border-b border-[#1E1E1E]">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 h-16 flex items-center gap-4 md:gap-8">
          <button onClick={() => nav("/admin/dashboard")} className="flex items-center shrink-0" data-testid="header-logo-home">
            <BrandMark size="lg" />
          </button>
          <div className="flex-1" />
          <span className="hidden md:inline-flex items-center font-mono text-[10px] tracking-[0.22em] text-[#71717A] uppercase">
            {todayLabel()}
          </span>
          {!isIC && (
            <nav className="hidden md:flex items-center gap-5">
              {TOP_TABS.map((t) => {
                const active = activeTop?.key === t.key;
                return (
                  <button key={t.key} onClick={() => nav(t.paths[0])}
                    data-testid={`top-tab-${t.key}`}
                    className={`flex items-center gap-2 text-sm transition-colors ${active ? "text-[#F4F4F5]" : "text-[#A1A1AA] hover:text-[#F4F4F5]"}`}>
                    <t.icon size={16} strokeWidth={1.6} className={active ? "text-[#F4F4F5]" : "text-[#71717A]"} />
                    <span>{t.label}</span>
                  </button>
                );
              })}
            </nav>
          )}
          <button onClick={() => { setShowSearch(true); setTimeout(() => inputRef.current?.focus(), 0); }}
            className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#262626] bg-[#0F0F0F] text-[#71717A] hover:border-[#3F3F46] hover:text-[#A1A1AA] text-xs transition-colors" data-testid="header-search">
            <Search size={13} /> <span>Search</span>
            <kbd className="ml-4 font-mono text-[10px] bg-[#1A1A1A] px-1.5 py-0.5 rounded border border-[#262626]">⌘K</kbd>
          </button>
          <div className="relative pl-3 border-l border-[#1E1E1E]">
            <button onClick={() => setMenu((m) => !m)} className="flex items-center gap-2.5" data-testid="header-user-chip">
              <div className="h-8 w-8 rounded-full bg-[#1A1A1A] border border-[#262626] grid place-items-center text-xs font-medium">{initial}</div>
              <div className="hidden md:block text-left leading-tight">
                <div className="text-xs text-[#F4F4F5]">{(user?.name || user?.email || "").split("@")[0]}</div>
                <div className="font-mono text-[9px] tracking-[0.22em] text-[#71717A] uppercase">{user?.role?.replace("_", " ")}</div>
              </div>
              <ChevronDown size={12} className="text-[#71717A]" />
            </button>
            {menu && (
              <div className="absolute right-0 mt-2 w-56 tt-card p-1 z-40" onMouseLeave={() => setMenu(false)}>
                <button onClick={() => { setMenu(false); nav("/admin/settings"); }} className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-[#1A1A1A]">Settings</button>
                {user?.role === "super_admin" && (
                  <>
                    <div className="tt-label px-3 py-1 mt-1">View as</div>
                    {["admin","role_head","employee","internal_committee"].map((r) => (
                      <button key={r} onClick={() => viewAs(r)} className="w-full text-left px-3 py-1.5 rounded-md text-xs hover:bg-[#1A1A1A] font-mono">{r}</button>
                    ))}
                  </>
                )}
                <div className="h-px bg-[#1E1E1E] my-1" />
                <button onClick={logout} className="w-full flex items-center gap-2 text-left px-3 py-2 rounded-md text-sm hover:bg-[#1A1A1A] text-[#A1A1AA]" data-testid="header-logout"><LogOut size={13} /> Sign out</button>
              </div>
            )}
          </div>
          <button className="tt-btn-ghost p-1.5 text-[#71717A] hover:text-[#F4F4F5]" data-testid="header-help"><HelpCircle size={15} strokeWidth={1.6} /></button>
          <button onClick={logout} className="tt-btn-ghost p-1.5 text-[#71717A] hover:text-[#F4F4F5]" data-testid="header-logout-icon"><LogOut size={15} strokeWidth={1.6} /></button>
        </div>
        {!isIC && (
          <nav className="md:hidden flex gap-5 px-4 py-2 border-t border-[#1E1E1E]" data-testid="mobile-top-nav">
            {TOP_TABS.map((t) => (
              <button key={t.key} onClick={() => nav(t.paths[0])} data-testid={`mobile-top-tab-${t.key}`}
                className={`flex items-center gap-1.5 text-sm ${activeTop?.key === t.key ? "text-[#F4F4F5]" : "text-[#A1A1AA]"}`}>
                <t.icon size={15} strokeWidth={1.6} /> {t.label}
              </button>
            ))}
          </nav>
        )}
        {activeTop && !isIC && (
          <div className="max-w-[1440px] mx-auto px-4 md:px-8">
            <div className="flex gap-1 border-t border-[#1E1E1E] overflow-x-auto">
              {SUB_TABS[activeTop.key].map((s) => (
                <NavLink key={s.to} to={s.to} data-testid={`sub-tab-${s.label.toLowerCase().replace(/\s+/g,'-')}`}
                  className={({ isActive }) => `px-3 py-2.5 text-sm whitespace-nowrap transition-colors border-b-2 -mb-px ${isActive ? "border-[#F4F4F5] text-[#F4F4F5]" : "border-transparent text-[#71717A] hover:text-[#F4F4F5]"}`}>
                  {s.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 min-w-0">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-6 md:py-8">
          <Outlet />
        </div>
      </main>

      {showSearch && (
        <div className="fixed inset-0 z-40 bg-[rgba(10,10,10,0.85)] flex items-start justify-center pt-32" onClick={() => setShowSearch(false)}>
          <div className="w-full max-w-xl tt-card p-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-2 py-1">
              <Search size={14} className="text-[#71717A]" />
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search candidates, employees, modules, forms…"
                className="flex-1 bg-transparent outline-none text-sm py-1.5" data-testid="search-input" />
              <kbd className="font-mono text-[10px] text-[#71717A]">esc</kbd>
            </div>
            {results && (
              <div className="mt-2 max-h-80 overflow-y-auto">
                {Object.entries(results).map(([k, v]) => v.length > 0 && (
                  <div key={k}>
                    <div className="tt-label px-2 mt-2 mb-1">{k}</div>
                    {v.map((r) => (
                      <button key={r.id} onClick={() => {
                        setShowSearch(false);
                        if (k === "candidates") nav("/admin/candidates"); else if (k === "employees") nav("/admin/team");
                        else if (k === "modules") nav("/admin/training/modules"); else if (k === "forms") nav("/admin/forms");
                      }} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#1A1A1A] text-sm flex justify-between">
                        <span>{r.label}</span><span className="text-xs font-mono text-[#71717A]">{r.sub}</span>
                      </button>
                    ))}
                  </div>
                ))}
                {Object.values(results).every((v) => v.length === 0) && <div className="text-sm text-[#71717A] px-2 py-2">No results.</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Reusable page header: eyebrow + title + subtitle + optional stats + primary action
export function PageHeader({ eyebrow, title, subtitle, stats = [], action, children }) {
  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          {eyebrow && <div className="font-mono text-[10px] tracking-[0.2em] text-[#71717A] uppercase mb-1">{eyebrow}</div>}
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <div className="text-sm text-[#A1A1AA] mt-1">{subtitle}</div>}
        </div>
        {action}
      </div>
      {stats.length > 0 && (
        <div className="flex flex-wrap gap-6 pb-5 mb-5 border-b border-[#1E1E1E]">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="tt-label mb-1">{s.label}</div>
              <div className={`font-mono text-xl ${s.accent === "pass" ? "text-[#22C55E]" : s.accent === "warn" ? "text-[#F5A524]" : s.accent === "danger" ? "text-[#EF4444]" : "text-[#F4F4F5]"}`}>{s.value}</div>
            </div>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}
